import type { Component, Control } from '@eformulieren/model'
import type { ReactNode } from 'react'
import type { ControlProps } from './kinds.js'

/**
 * Wat er nog niet is, staat er als gat met een reden.
 *
 * Niet een leeg vak en niet een half werkende vorm. Een half veld leest als een
 * storing, en een leeg vak laat de invuller zoeken naar iets wat er niet is. Deze
 * plaatshouder zegt wat er komt en wanneer, en dezelfde tabel is de bron van de
 * dekkingslijst: elk controltype uit de catalogus heeft óf een component óf een
 * regel hier, en een test houdt dat vast (`registry.test.tsx`).
 *
 * Twee namen staan er met opzet in en blijven leeg: `begroting` en `cofinanciering`.
 * De control zelf is buiten scope tot de U10-sessie met NWO is geweest (ontwerp 6.2),
 * en er komt tot die tijd ook geen voorlopige vorm. Wat blijft is de gereserveerde
 * naam in de componentenlijst, zodat toevoegen later geen modelwijziging vraagt.
 */

export interface PendingEntry {
  /**
   * De milestone die deze vorm levert, of `null` als er nog geen is.
   *
   * **M3 staat er niet meer bij, en dat is geen opschoning maar een grens.** Tot M5.17b
   * wachtten `status`, `notification`, `progress` en `timer` op M3, en die is af: ze zijn daar
   * niet gebouwd, dus stond er een belofte die het product niet meer kon nakomen. Een
   * plaatshouder mag daarom niet naar een afgeronde milestone wijzen; wat er niet is toegewezen
   * draagt `null` en zegt dat.
   */
  readonly milestone: 'M6' | 'M7' | 'M8' | null
  readonly reden: { readonly nl: string; readonly en: string }
}

export const WAITING_ON: Partial<Record<Control, PendingEntry>> = {
  // `criterium` stond hier tot M7.3 met milestone M7; die control is nu gebouwd
  // (`Criterium.tsx`), net als `verwijzing` in M6.2. Dezelfde regel geldt: een gebouwde
  // control die hier blijft staan wordt als plaatshouder getekend, en daar staat een test op.
  //
  // `verwijzing` stond hier tot M6.2 met milestone M6; die control is nu gebouwd
  // (`Verwijzing.tsx`) en hoort daarom niet meer in deze lijst. Een gebouwde control die hier
  // blijft staan zou als plaatshouder worden getekend, want de registry vult deze lijst als
  // laatste in; daar staat een test op.
  component: {
    milestone: 'M7',
    reden: {
      nl: 'Een vast component is als geheel plaatsbaar en brengt zijn eigen presentatie mee.',
      en: 'A fixed component is placed as a whole and brings its own presentation.',
    },
  },
  status: {
    // Geen milestone: de snedelijst van de sprintplanning houdt de plek in de catalogus vrij
    // (M5.17b, melding 10).
    milestone: null,
    reden: {
      nl: 'Een statuslabel toont de stand van de instantie en die komt van de runtime.',
      en: 'A status label shows the state of the instance, which comes from the runtime.',
    },
  },
  notification: {
    milestone: null,
    reden: {
      nl: 'Een melding uit de validatie hoort bij de schil van de runtime, niet bij een veld.',
      en: 'A validation notice belongs to the runtime shell, not to a field.',
    },
  },
  progress: {
    milestone: null,
    reden: {
      nl: 'Een voortgangsbalk toont de voortgang van de instantie en die komt van de runtime.',
      en: 'A progress bar shows the progress of the instance, which comes from the runtime.',
    },
  },
  timer: {
    milestone: null,
    reden: {
      nl: 'Een doorlooptijdmechanisme hangt aan de deadline van de instantie.',
      en: 'A timer hangs on the deadline of the instance.',
    },
  },
}

/** De componentnamen die op de U10-sessie wachten en waarvoor niets wordt gebouwd. */
export const OUTSIDE_SCOPE: readonly Component[] = ['begroting', 'cofinanciering', 'lening']

/**
 * Wat de invuller ziet: één neutrale zin, zonder controlnaam, componentnaam, milestone of
 * scope (audit van 30 oktober 2026). Daar stond "component: publicatielijst … (M7)" en "Deze vorm
 * is buiten scope tot de begrotingssessie met NWO is geweest": ontwikkeltekst die voor een
 * aanvrager niets betekent en hem laat denken dat het formulier stuk is. Waarom een vorm er nog
 * niet is staat hierboven in `WAITING_ON` en `OUTSIDE_SCOPE`, voor wie het product bouwt, en in
 * `data-wacht-op` voor wie de pagina onderzoekt; de lezer krijgt het niet.
 */
export function Placeholder({ veld: field, taal: language, koppeling: link }: ControlProps): ReactNode {
  const wait = WAITING_ON[field.control]
  const component = field.control === 'component' && 'component' in field ? field.component : undefined
  const outsideScope = component !== undefined && OUTSIDE_SCOPE.includes(component)
  const reason = outsideScope ? 'U10' : (wait?.milestone ?? 'geen')

  return (
    <p
      className="ef-plaatshouder"
      id={link.id}
      aria-describedby={link['aria-describedby']}
      data-wacht-op={reason}
      data-vorm={component === undefined ? field.control : `${field.control}:${component}`}
    >
      {language === 'nl'
        ? 'Dit onderdeel is nog niet beschikbaar. Hier hoeft nu niets ingevuld te worden.'
        : 'This part is not available yet. Nothing needs to be filled in here for now.'}
    </p>
  )
}
